import { v4 as uuid } from 'uuid';
import { schema } from '@/lib/schema/loader';
import { emptyMetadata, type ObservationRecord } from './types';

/**
 * Factory for a fresh observation draft. Split from `ObservationContext.tsx`
 * so that module exports only React components (required for Fast Refresh).
 *
 * `schemaVersion` is sourced from the loaded schema's metadata so that
 * exports always label themselves with the version that scored them —
 * avoids silent drift if the rubric JSON bumps.
 */
export function emptyRecord(id: string = uuid()): ObservationRecord {
  const now = Date.now();
  return {
    id,
    instrumentId: 'FCCERS-3',
    schemaVersion: schema._metadata.schema_version as '1.0.0',
    status: 'draft',
    metadata: emptyMetadata(),
    responses: {},
    itemNa: {},
    itemNotes: {},
    itemStatus: {},
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Bring a record loaded from IndexedDB up to the current metadata shape.
 *
 * Drafts persist across releases, so a record written before a field existed
 * comes back missing it. Reading `metadata.enrollmentByAge.m0_5` on such a
 * record threw and took the whole metadata page down — an observer with a
 * half-finished observation could not reopen it. Layering the stored values
 * over `emptyMetadata()` fills every gap, and the nested objects are merged
 * rather than replaced so a partially-filled age breakdown survives too.
 *
 * Unknown extra keys are preserved: they cost nothing and may belong to a
 * newer release the user has since rolled back from.
 */
export function migrateRecord(record: ObservationRecord): ObservationRecord {
  const base = emptyMetadata();
  const stored = (record.metadata ?? {}) as Partial<typeof base>;
  return {
    ...record,
    metadata: {
      ...base,
      ...stored,
      enrollmentByAge: {
        ...base.enrollmentByAge,
        ...(stored.enrollmentByAge ?? {}),
      },
      specialNeedTypes: stored.specialNeedTypes ?? base.specialNeedTypes,
      ageGroupsPresent: stored.ageGroupsPresent ?? base.ageGroupsPresent,
    },
    responses: record.responses ?? {},
    itemNa: record.itemNa ?? {},
    itemNotes: record.itemNotes ?? {},
    itemStatus: record.itemStatus ?? {},
  };
}
